import { Element, useEditor, useNode } from "@craftjs/core";
import { useState } from "react";

import { CButton } from "./CButton";
import { CContainer } from './CContainer';
import { CText } from "./CText";
import { CLink } from "./CLink";
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from "@mui/material";

interface CDrawUpModalProps {
  openButtonText?: any,
  closeButtonText?: any,
  direction?: any,
  openButtonType?: any;
  [key: string]: any;
}

export const CDrawUpModal = ({ openButtonText, closeButtonText, direction, openButtonType, ...props }: CDrawUpModalProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const { enabled } = useEditor(
    (state, query) => ({
      enabled: state.options.enabled,
    })
  );

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <CContainer 
      ref={(ref: any) => connect(drag(ref))} 
      width="100%"
      {...props}
    >
      <CContainer
        paddingH={10}
        paddingV={0}
        flexDirection="row"
        verticalAlign="center"
        horizontalAlign="center"
        data-cy="frame-container"
        id="modal_open_btn_wrapper"
      >
        <Box 
          style={{ width: "100%", textAlign: 'center' }} 
          onClick={handleOpen}
        >
          {
            openButtonType == 'link' ? (
              <Element
                canvas
                is={CLink}
                id="modal_open_link"
                text={openButtonText} 
                fontSize={18} 
                fontWeight={800} 
                color="#00571f"
              />
            ) : (
              <Element
                canvas
                is={CButton}
                id="modal_open_btn"
                text={openButtonText} 
                fontSize={18} 
                fontWeight={800} 
                width="100%" 
                height="60px" 
                backgroundColor="#00571f" 
                borderRadius="200px" 
                color="#FFFFFF"
              />
            )
          }
        </Box>
      </CContainer>
      <Stack style={
        enabled ? {
          opacity: 0.5
        } : {
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100%',
          padding: '20px 0',
          backgroundColor: '#fff',
          minHeight: '100%',
          height: '100vh',
          overflowY: 'scroll',
          zIndex: '100',
          transform: open ? 'translateX(0)' : `translateX(${direction == 'ltr' ? '-' : ''}100%)`,
          transition: 'transform .3s ease-in-out',
        }
      }>
        <CContainer
          paddingH={10}
          paddingV={0}
          flexDirection="Column"
          verticalAlign="center"
          horizontalAlign="center"
          data-cy="frame-container"
          id="modal_wrapper"
        >
          <Element
            canvas
            is={CContainer}
            paddingH={10}
            paddingV={0}
            flexDirection="Column"
            verticalAlign="center"
            horizontalAlign="center"
            data-cy="frame-container"
            height="100px"
            id="modal_content_wrapper"
          >
            <CText textAlign="center" variant="p" fontSize={16} color="#323232" text="Modal Content. Please Drag & Drop Contents." data-cy="frame-text" />
          </Element>
          <Box 
            style={{ width: "100%" }} 
            onClick={handleClose}
          >
            <Element 
              canvas
              is={CButton}
              id="modal_close_btn"
              text={closeButtonText} 
              fontSize={18}
              fontWeight={800} 
              width="100%" 
              height="60px" 
              backgroundColor="#FFFFFF" 
              color="#00571f"
              borderRadius="200px" 
              borderWidth="2px"
              borderColor="#00571f"
            />
          </Box>
        </CContainer>
      </Stack>
    </CContainer>
  );
};

export const CDrawUpModalProps: CDrawUpModalProps = {
  openButtonText: "Open",
  closeButtonText: "Close",
  direction: "ltr",
  openButtonType: "button",
};

export const CDrawUpModalSettings = () => {
  const {
    actions: { setProp },
    direction,
    props,
    openButtonType,
  } = useNode((node) => ({
    props: node.data.props,
    direction: node.data.props.gap,
    openButtonType: node.data.props.openButtonType,
  }));

  return (
    <Stack spacing={2} paddingBottom={4}>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Open Direction</FormLabel>
        <RadioGroup
          defaultValue={direction}
          onChange={(e) =>
            setProp((props: any) => (props.direction = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '10px' }}
        >
          <FormControlLabel
            label="Left To Right"
            value="ltr"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Right To Left"
            value="rtl"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Open Button Type</FormLabel>
        <RadioGroup
          defaultValue={openButtonType}
          onChange={(e) =>
            setProp((props: any) => (props.openButtonType = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '10px' }}
        >
          <FormControlLabel
            label="Button"
            value="button"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Link"
            value="link"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};


CDrawUpModal.craft = {
  displayName: "DrawUpModal",
  props: CDrawUpModalProps,
  related: {
    settings: CDrawUpModalSettings
  }
};
